/* Copyright (c) 2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, FlexContainer, Input, theme } from "@nordcloud/gnui";
import { isNotEmpty } from "~/tools";
import { KEY_VALUE_SEPARATOR } from "./helpers";

type Props = {
  inputTags?: string[];
  onChange: (tags: string[]) => void;
  width?: string;
  placeholder?: {
    key: string;
    value: string;
  };
  showIcon?: boolean;
};

enum KEY_VALUE {
  KEY = 0,
  VALUE = 1,
}

const emptyTag = ["", ""];

export function TagsInput({
  inputTags,
  onChange,
  width,
  placeholder,
  showIcon = false,
}: Props) {
  const [tags, setTags] = useState<string[][]>(getTags(inputTags));

  useEffect(() => {
    setTags(getTags(inputTags));
  }, [inputTags, setTags]);

  const onChangeHandler = (value: string, index: number, field: KEY_VALUE) => {
    const newTags = tags.map((tag, i) =>
      i === index
        ? [...tag.slice(0, field), value, ...tag.slice(field + 1)]
        : tag
    );
    setTags(newTags);
    onChange(
      newTags
        .filter((tag) => isNotEmpty(tag.join("")))
        .map((tag) => tag.join(KEY_VALUE_SEPARATOR))
    );
  };

  const onRemoveTagHandler = (index: number) => {
    const newTags = tags.filter((_, i) => i !== index);
    setTags(newTags);
    onChange(
      newTags
        .filter((tag) => isNotEmpty(tag.join("")))
        .map((tag) => tag.join(KEY_VALUE_SEPARATOR))
    );
  };

  return (
    <>
      {tags?.map((tag, index) => (
        <FlexContainer
          key={`tag-pair-${index}`}
          alignItems="center"
          columnGap={theme.spacing.spacing02}
          marginBottom={theme.spacing.spacing02}
        >
          <InputWrapper width={width}>
            <Input
              value={tag[0] ?? ""}
              placeholder={placeholder?.key ?? undefined}
              onChange={(e) =>
                onChangeHandler(e.target.value, index, KEY_VALUE.KEY)
              }
            />
          </InputWrapper>
          <InputWrapper width={width}>
            <Input
              name={tag[0] ?? ""}
              value={tag[1] ?? ""}
              placeholder={placeholder?.value ?? undefined}
              onChange={(e) =>
                onChangeHandler(e.target.value, index, KEY_VALUE.VALUE)
              }
            />
          </InputWrapper>
          <Button
            severity="low"
            icon="trash"
            onClick={() => onRemoveTagHandler(index)}
          />
        </FlexContainer>
      ))}
      <Button
        severity="low"
        icon={showIcon ? "plus" : undefined}
        onClick={() => setTags([...tags, [...emptyTag]])}
      >
        Add Tag
      </Button>
    </>
  );
}

const InputWrapper = styled.div<{ width?: string }>`
  width: ${({ width }) => width};
`;

function getTags(inputTags: string[] | undefined) {
  return inputTags?.map((tag) => tag.split(KEY_VALUE_SEPARATOR)) ?? [];
}
